import { authFetch } from './http';
import type {
  ClientsApiResponse,
  Client,
  ClientContact,
  ClientsResponseData,
} from './types';

// Re-export so other files can import from either place
export type { ClientsApiResponse, Client, ClientContact, ClientsResponseData };

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/v1`
  : '/efx-csp/api/v1';

export interface GetClientsParams {
  email?: string;
  phone?: string;
  fxboId?: string;
  page: number;
  size: number;
}

export async function getClients(params: GetClientsParams): Promise<ClientsApiResponse> {
  const query = new URLSearchParams();
  if (params.email?.trim()) query.set('email', params.email.trim());
  if (params.phone?.trim()) query.set('phone', params.phone.trim());
  if (params.fxboId?.trim()) query.set('fxboId', params.fxboId.trim());
  query.set('page', String(params.page));
  query.set('size', String(params.size));

  const url = `${apiBaseUrl}/clients?${query.toString()}`;
  try {
    const res = await authFetch(url, {
      method: 'GET',
      headers: {
        Accept: '*/*',
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        message: data?.message ?? `Request failed: ${res.status} ${res.statusText}`,
      };
    }

    return data as ClientsApiResponse;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'A network error occurred.';
    return { success: false, message };
  }
}

// ─── Shared response shape ───────────────────────────────────────────────────

export interface PhoneChangeApiResponse {
  success: boolean;
  message?: string;
  dateTime?: string;
  data?: {
    firstName?: string;
    lastName?: string;
    oldValue?: string;
    newValue?: string;
    reason?: string;
    status?: string;
  };
}

// ─── Delete phone ─────────────────────────────────────────────────────────────

export interface DeletePhoneParams {
  fxbold: string;
  contactValue: string;
  reason: string;
}

export async function deletePhone(params: DeletePhoneParams): Promise<PhoneChangeApiResponse> {
  const query = new URLSearchParams();
  query.set('contactValue', params.contactValue);
  query.set('reason', params.reason);

  const url = `${apiBaseUrl}/phone-change/${encodeURIComponent(params.fxbold)}/delete?${query.toString()}`;

  try {
    const res = await authFetch(url, {
      method: 'DELETE',
      headers: {
        Accept: '*/*',
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return {
        success: false,
        message: data?.message ?? `Request failed: ${res.status} ${res.statusText}`,
      };
    }

    return data as PhoneChangeApiResponse;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'A network error occurred.';
    return { success: false, message };
  }
}

// ─── Swap phones (primary ↔ secondary) ───────────────────────────────────────

export interface SwapPhoneParams {
  fxbold: string;
  reason?: string;
}

export async function swapPhones(params: SwapPhoneParams): Promise<PhoneChangeApiResponse> {
  const query = new URLSearchParams();
  if (params.reason?.trim()) query.set('reason', params.reason.trim());
  const queryString = query.toString();
  const url = `${apiBaseUrl}/phone-change/${encodeURIComponent(params.fxbold)}/swap${queryString ? `?${queryString}` : ''}`;
  try {
    const res = await authFetch(url, {
      method: 'POST',
      headers: {
        Accept: '*/*',
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return {
        success: false,
        message: data?.message ?? `Request failed: ${res.status} ${res.statusText}`,
      };
    }

    return data as PhoneChangeApiResponse;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'A network error occurred.';
    return { success: false, message };
  }
}

// ─── Update phone ─────────────────────────────────────────────────────────────

export interface UpdatePhoneParams {
  fxbold: string;
  oldValue: string;
  newValue: string;
  reason: string;
}

export async function updatePhone(params: UpdatePhoneParams): Promise<PhoneChangeApiResponse> {
  const url = `${apiBaseUrl}/phone-change/${encodeURIComponent(params.fxbold)}/update`;
  try {
    const res = await authFetch(url, {
      method: 'PUT',
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        oldValue: params.oldValue,
        newValue: params.newValue,
        reason: params.reason,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return {
        success: false,
        message: data?.message ?? `Request failed: ${res.status} ${res.statusText}`,
      };
    }

    return data as PhoneChangeApiResponse;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'A network error occurred.';
    return { success: false, message };
  }
}

// ─── Add phone ────────────────────────────────────────────────────────────────

export interface AddPhoneParams {
  fxbold: string;
  contactValue: string;
  reason?: string;
  isPrimary?: boolean;
}

export async function addPhone(params: AddPhoneParams): Promise<PhoneChangeApiResponse> {
  const url = `${apiBaseUrl}/phone-change/add`;
  try {
    const res = await authFetch(url, {
      method: 'POST',
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fxboId: Number(params.fxbold),
        contact: params.contactValue,
        type: 'PHONE',
        reason: params.reason,
        isPrimary: params.isPrimary,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return {
        success: false,
        message: data?.message ?? `Request failed: ${res.status} ${res.statusText}`,
      };
    }

    return data as PhoneChangeApiResponse;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'A network error occurred.';
    return { success: false, message };
  }
}